import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import NumericInput from "src/main/PointOfSale/CurrentSalePanel/NumericInput"

import Form from "src/components/Form"
import Modal from "src/components/Modal"

import { MIN_FUEL_GALLONS } from "../AddOns/launchAddOnPricing"

export const fillUpAddOns = (addons) =>
  (addons || []).filter((addon) => addon.fuelMode === "fill_up")

const LaunchFuelQuantityModal = ({
  addons,
  isOpen,
  isSubmitting,
  onCancel,
  onConfirm,
}) => {
  const items = fillUpAddOns(addons)
  const [quantities, setQuantities] = useState({})
  const [validationAttempted, setValidationAttempted] = useState(false)

  // A second launch reuses the mounted modal, so last launch's gallons would
  // otherwise still be sitting in the field.
  useEffect(() => {
    if (isOpen) {
      setQuantities({})
      setValidationAttempted(false)
    }
  }, [isOpen])

  const isMeasured = (addon) => quantities[addon.id] >= MIN_FUEL_GALLONS
  const allMeasured = items.every(isMeasured)

  const handleConfirm = () => {
    if (!allMeasured) {
      setValidationAttempted(true)
      return
    }

    onConfirm(
      items.map((addon) => ({
        launch_addon_id: addon.id,
        quantity: quantities[addon.id],
      }))
    )
  }

  const renderItem = (addon) => {
    const showError = validationAttempted && !isMeasured(addon)

    return (
      <div
        key={addon.id}
        className="flex items-start justify-between gap-4 py-3"
      >
        <div>
          <p className="mb-0 font-semibold text-gray-900">{addon.name}</p>
          <p className="mb-0 text-sm text-gray-600">
            Full tank requested · {addon.unitPriceFormatted} /{" "}
            {addon.fuelUnitSymbol}
          </p>
        </div>
        <div className="w-32 shrink-0">
          <NumericInput
            id={`launch-fuel-quantity-${addon.id}`}
            ariaLabel={`Amount of ${addon.name} in ${addon.fuelUnitSymbol}`}
            value={quantities[addon.id] ?? null}
            onChange={(quantity) =>
              setQuantities((current) => ({ ...current, [addon.id]: quantity }))
            }
            precision={5}
            // Deliberately not MIN_FUEL_GALLONS: NumericInput clamps up to its
            // floor on blur, so a mistaken 0 would silently become a billable
            // sliver instead of failing the check below.
            minValue={0}
            icon={addon.fuelUnitSymbol}
            iconPosition="right"
            hasErrors={showError}
          />
          {showError && <Form.Error>Enter how much was pumped.</Form.Error>}
        </div>
      </div>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onCancel} size="mediumFixed">
      <Modal.Header title="How much fuel?" />
      <Modal.Body>
        <p className="text-gray-700">
          Enter the amount pumped so the sale can be priced. The launch still
          shows what the boater asked for.
        </p>
        <div className="divide-y divide-gray-200">{items.map(renderItem)}</div>
      </Modal.Body>
      <Modal.Footer
        confirmBtnLoading={isSubmitting}
        confirmBtnText="Check out"
        disableCancelBtn={isSubmitting}
        onClose={onCancel}
        onSubmit={handleConfirm}
      />
    </Modal>
  )
}

LaunchFuelQuantityModal.propTypes = {
  addons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      fuelMode: PropTypes.string,
      fuelUnitSymbol: PropTypes.string,
      unitPriceFormatted: PropTypes.string,
    })
  ),
  isOpen: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
}

export default LaunchFuelQuantityModal
